import { reactive } from 'vue'

const authPath = typeof window !== 'undefined' && /^\/(futu|longbridge|usmart)-auth\/?$/.test(window.location.pathname)
const parentParam = typeof window === 'undefined' ? '' : new URLSearchParams(window.location.search).get('web_parent_origin')
const restricted = Boolean(authPath && parentParam)
let parentOrigin = ''
// 本地 PC 弹窗与开窗沿用相同宿主来源约束，开发模式允许内网 HTTP，部署环境仍要求 HTTPS。
try {
  const url = new URL(parentParam)
  const localHost = ['localhost', '[::1]'].includes(url.hostname) || /^(?:(?:127|10)(?:\.\d{1,3}){3}|192\.168(?:\.\d{1,3}){2}|172\.(?:1[6-9]|2\d|3[01])(?:\.\d{1,3}){2})$/.test(url.hostname)
  const localDevelopment = import.meta.env.DEV && import.meta.env.MODE === 'development' && window.location.protocol === 'http:' && localHost
  if (url.protocol === 'https:' || (url.protocol === 'http:' && (['localhost', '127.0.0.1'].includes(url.hostname) || localDevelopment))) parentOrigin = url.origin
} catch {}
let token = ''
let hostWindow = null
let initialization = null
let removeListener = () => {}
export const brokerWebContext = reactive({ restricted, ready: false, error: '', lang: '', theme: 'light', deviceId: '', deviceInfo: '', completed: false })

// isBrokerWebAuthorization 浏览器入口不回退到 App Cookie 或 bridge 登录槽位。
export const isBrokerWebAuthorization = () => restricted
// getBrowserBrokerToken 登录态只在当前窗口内存使用，调用已有券商接口。
export const getBrowserBrokerToken = () => token
// clearBrowserBrokerToken 退出时清理内存，不撤销已经建立的券商连接。
export const clearBrowserBrokerToken = () => { token = ''; brokerWebContext.ready = false; removeListener() }
// brokerWebError 对浏览器只显示本地化键，不输出请求头或第三方原始异常。
export const brokerWebError = () => new Error('brokerWeb.reopen')

// initializeBrokerWebAuthorization PC iframe 使用 parent、移动开窗使用 opener，均严格核验宿主窗口和精确 origin。
export const initializeBrokerWebAuthorization = () => {
  if (initialization) return initialization
  initialization = new Promise((resolve) => {
    if (!restricted) { resolve(); return }
    document.documentElement.classList.add('broker-web-authorization')
    hostWindow = window.parent && window.parent !== window ? window.parent : window.opener
    if (!parentOrigin || !hostWindow) { brokerWebContext.error = 'reopen'; resolve(); return }
    let timer
    const listener = async (event) => {
      if (event.source !== hostWindow || event.origin !== parentOrigin || event.data?.type !== 'MOBIUS_WEB_AUTH_CONTEXT' || !event.data.token) return
      token = event.data.token
      brokerWebContext.lang = event.data.language || 'US'
      brokerWebContext.deviceId = event.data.deviceId || ''
      brokerWebContext.deviceInfo = event.data.deviceInfo || ''
      brokerWebContext.theme = event.data.colorScheme === 'dark' ? 'dark' : 'light'
      document.documentElement.dataset.brokerTheme = brokerWebContext.theme
      const { default: i18n } = await import('@/locales')
      const { normalizeLocale } = await import('@/locales/localeConfig')
      i18n.global.locale.value = normalizeLocale(brokerWebContext.lang)
      clearTimeout(timer)
      brokerWebContext.ready = true; brokerWebContext.error = ''
      resolve()
    }
    window.addEventListener('message', listener)
    removeListener = () => { clearTimeout(timer); window.removeEventListener('message', listener) }
    timer = setTimeout(() => { brokerWebContext.error = 'reopen'; removeListener(); resolve() }, 10000)
    hostWindow.postMessage({ type: 'MOBIUS_WEB_AUTH_CONTEXT_REQUEST' }, parentOrigin)
  })
  return initialization
}
// completeBrokerWebAuthorization 区分授权成功与普通退出；通知宿主后由其回查真实连接并展示动画。
export const completeBrokerWebAuthorization = (authorized = false) => {
  if (brokerWebContext.completed) return
  const parent = hostWindow
  const embedded = window.parent && window.parent !== window
  if (parent && !parent.closed && parentOrigin) {
    try { parent.postMessage({ type: 'MOBIUS_BROKER_AUTH_FINISHED', status: authorized ? 'SUCCESS' : 'CLOSED' }, parentOrigin) } catch { /* 宿主窗口失效时仍允许退出授权页。 */ }
    if (!embedded) { try { parent.focus() } catch { /* 部分浏览器限制跨窗口聚焦，继续关闭当前窗口。 */ } }
  }
  clearBrowserBrokerToken()
  brokerWebContext.completed = true
  if (!embedded) window.close()
}
if (restricted) window.addEventListener('pagehide', clearBrowserBrokerToken, { once: true })
